<template>
  <settings-page-card>
    <div class="flex items-center justify-between px-6">
      <h3 class="text-2xl font-semibold mb-0">
        Invite User
      </h3>
    </div>

    <div class="flex flex-col mt-8 px-6">
      <b-form @submit.prevent="handleSubmit">
        <b-form-group label="Name">
          <b-input
            v-model="name"
            :disabled="loading"
            :state="validationState('name')"
            placeholder="Full name"
            required
            @blur="$v.name.$touch()"
          />
          <b-form-invalid-feedback v-if="$v.name.$error">
            <span v-if="!$v.name.required">Name is required.</span>
            <span v-else-if="!$v.name.minLength">Name must be at least 2 characters.</span>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Email">
          <b-input
            v-model="email"
            :disabled="loading"
            :state="validationState('email')"
            type="email"
            placeholder="name@company.com"
            required
            @blur="$v.email.$touch()"
          />
          <b-form-invalid-feedback v-if="$v.email.$error">
            <span v-if="!$v.email.required">Email is required.</span>
            <span v-else-if="!$v.email.email">Please enter a valid email address.</span>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="Role Selection"
          class="mb-4"
        >
          <v-select
            v-model="role"
            :options="roles"
            :reduce="val => val.id"
            label="name"
            :disabled="loading"
            placeholder="Select a role"
            @blur="$v.role.$touch()"
          />
          <div
            v-if="$v.role.$error"
            class="text-sm text-red-600 mt-1"
          >
            {{ roleErrorMessage() }}
          </div>
        </b-form-group>

        <div class="flex items-center justify-end">
          <b-btn
            variant="primary"
            class="!bg-[#4F83E3] hover:!bg-[#3967d7]"
            type="submit"
            :disabled="isSubmitDisabled"
          >
            Invite User
          </b-btn>
        </div>
      </b-form>
    </div>
  </settings-page-card>
</template>

<script>
import SettingsPageCard from '@/layouts/dashboard/SettingsPageCard.vue'
import {mapActions, mapState} from 'vuex'
import { required, minLength, email as emailValidator } from 'vuelidate/lib/validators'

export default {
  name: 'ManageRoles',
  components: {SettingsPageCard},
  data () {
    return {
      name: '',
      email: '',
      role: null,
      roles: []
    }
  },
  computed: {
    ...mapState('roles', {
      loading: state => state.loading
    }),
    isSubmitDisabled () {
      return this.loading || this.$v.$invalid
    }
  },
  validations: {
    name: {
      required,
      minLength: minLength(2)
    },
    email: {
      required,
      email: emailValidator
    },
    role: {
      required
    }
  },
  methods: {
    ...mapActions({
      getRoles: 'roles/getRoles',
      createMember: 'roles/createMember'
    }),
    validationState (field) {
      const v = this.$v[field]
      if (!v) return null
      if (!v.$dirty) return null
      return !v.$invalid
    },
    roleErrorMessage () {
      if (!this.$v.role.$dirty) return ''
      return this.$v.role.required === false ? 'Please select a role.' : ''
    },
    async handleSubmit () {
      try {
        this.$v.$touch()

        if (this.$v.$invalid) {
          await this.$swal.fire({
            icon: 'warning',
            iconColor: '#4F83E3',
            title: 'Please fix the highlighted fields',
            showConfirmButton: false,
            timer: 1800
          })
          return
        }

        {
          await this.createMember({
            name: this.name,
            email: this.email,
            role_id: this.role
          })

          await this.$swal.fire({
            icon: 'success',
            iconColor: '#4F83E3',
            title: 'Invitation sent',
            text: `We emailed ${this.email} an invite to join.`,
            showConfirmButton: false,
            timer: 1800
          })

          this.name = ''
          this.email = ''
          this.role = null
          this.$v.$reset()

          setTimeout(() => this.$router.push({ name: 'settings.permissions.users' }), 1000)
        }
      } catch (err) {
        console.error(err)
        await this.$swal.fire({
          icon: 'error',
          iconColor: '#4F83E3',
          title: err.response?.data?.message || 'Error while creating user',
          showConfirmButton: false,
          timer: 3000
        })
      }
    }
  },
  async mounted () {
    await this.$store.dispatch('projects/hasAccessToPage', 'members.create')
    this.roles = await this.getRoles()
  }
}
</script>
<style scoped>
/* Hide Bootstrap's default valid/invalid background icons while keeping color borders */
.form-control.is-valid,
.form-control.is-invalid,
.custom-select.is-valid,
.custom-select.is-invalid {
  background-image: none !important;
  padding-right: 0.75rem !important;
}
</style>